import { type EventDto, type ThreadLive, type ThreadSummary, type ToolName } from "@nabla/api";

export interface Progress {
  readonly turn: number;
  readonly tools: readonly { readonly tool: ToolName; readonly object?: string }[];
  readonly text: string;
}

type News = NonNullable<ThreadSummary["news"]>;

export interface Threads {
  putThread(thread: ThreadSummary): void;
  thread(id: string): ThreadSummary | undefined;
  threads(): readonly ThreadSummary[];
  progress(id: string): Progress | undefined;
  live(id: string): ThreadLive | undefined;
  mine(): readonly ThreadSummary[];
  news(thread: ThreadSummary): News | undefined;
  saw(thread: ThreadSummary): boolean;
}

type Heard = Extract<EventDto, { readonly type: "conversa-parcial" | "conversa-estado" }>;

function progressed(held: Progress | undefined, { turn, part }: Extract<Heard, { readonly type: "conversa-parcial" }>): Progress {
  const base = held && held.turn === turn ? held : { turn, tools: [], text: "" };
  if (part.kind === "texto") return { ...base, text: base.text === "" ? part.text : `${base.text}\n\n${part.text}` };
  return { ...base, tools: [...base.tools, { tool: part.tool, ...(part.object ? { object: part.object.name } : {}) }] };
}

function createHeard(touch: (topic: string) => void) {
  const partials = new Map<string, Progress>();
  const lives = new Map<string, ThreadLive>();
  return {
    progress: (id: string) => partials.get(id),
    live: (id: string) => lives.get(id),
    heard(event: Heard): void {
      if (event.type === "conversa-estado") lives.set(event.thread, event.live);
      else partials.set(event.thread, progressed(partials.get(event.thread), event));
      touch(`thread:${event.thread}`);
    },
    moved(thread: ThreadSummary): void {
      if (thread.state !== "pensando") partials.delete(thread.id);
      if (thread.state === "pronta" || thread.state === "falhou") lives.delete(thread.id);
    },
    unheard(): void {
      for (const id of new Set([...partials.keys(), ...lives.keys()])) touch(`thread:${id}`);
      partials.clear();
      lives.clear();
    },
  };
}

interface ThreadsDeps {
  readonly touch: (topic: string) => void;
  readonly listed: () => readonly ThreadSummary[] | undefined;
}

const fresher = (held: ThreadSummary | undefined, thread: ThreadSummary) =>
  held === undefined || (thread.seq >= held.seq && thread.marked >= held.marked);

export function createThreads({ touch, listed: snapshot }: ThreadsDeps) {
  const threads = new Map<string, ThreadSummary>();
  const seen = new Map<string, number>();
  const heard = createHeard(touch);
  let threadList: readonly ThreadSummary[] | undefined;
  let mineList: readonly ThreadSummary[] | undefined;
  let mineOf: readonly ThreadSummary[] | undefined;
  const news = (thread: ThreadSummary): News | undefined => ((seen.get(thread.id) ?? 0) >= thread.seq ? undefined : thread.news);
  const listed = (thread: ThreadSummary) => thread.mine !== undefined && (thread.archived === undefined || news(thread) === "arquivada");
  const overlaid = (item: ThreadSummary) => (fresher(threads.get(item.id), item) ? item : (threads.get(item.id) ?? item));
  const moved = () => {
    threadList = undefined;
    mineList = undefined;
    touch("threads");
  };
  const members: Threads = {
    putThread(thread) {
      if (!fresher(threads.get(thread.id), thread)) return;
      threads.set(thread.id, thread);
      heard.moved(thread);
      touch(`thread:${thread.id}`);
      moved();
    },
    thread: (id) => threads.get(id),
    threads: () => (threadList ??= [...threads.values()].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))),
    progress: heard.progress,
    live: heard.live,
    mine() {
      const items = snapshot();
      if (items !== mineOf) [mineOf, mineList] = [items, undefined];
      return (mineList ??= (items ?? []).map(overlaid).filter(listed));
    },
    news,
    saw(thread) {
      if (news(thread) === undefined && (seen.get(thread.id) ?? 0) >= thread.seq) return false;
      seen.set(thread.id, thread.seq);
      touch("vistas");
      touch(`thread:${thread.id}`);
      moved();
      return true;
    },
  };
  return {
    members,
    topics: () => [...[...threads.keys()].map((id) => `thread:${id}`), "threads"],
    signedIn(): void {
      mineList = undefined;
    },
    heard: heard.heard,
    unheard: heard.unheard,
    forget(): void {
      threads.clear();
      seen.clear();
      heard.unheard();
      threadList = undefined;
      mineList = undefined;
    },
  };
}
